import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from "../../utils/constant";

const RiskKanbanView = ({ orgId, categories, severityLevels, handleSeverityUpdate, users }) => {
    const [risks, setRisks] = useState([]);
    const [loading, setLoading] = useState(true);

    const riskStatuses = [
        { value: 'notStarted', label: 'Not Started', class: 'secondary', icon: 'fa-clock' },
        { value: 'inProgress', label: 'In Progress', class: 'primary', icon: 'fa-spinner' },
        { value: 'retest', label: 'Retest', class: 'warning', icon: 'fa-sync' },
        { value: 'falsePositive', label: 'False Positive', class: 'info', icon: 'fa-ban' },
        { value: 'riskAccepted', label: 'Risk Accepted', class: 'warning', icon: 'fa-check-circle' },
        { value: 'remediated', label: 'Remediated', class: 'success', icon: 'fa-shield-alt' },
        { value: 'completed', label: 'Completed', class: 'success', icon: 'fa-check-double' }
    ];

    useEffect(() => {
        fetchRisks();
    }, []);

    const fetchRisks = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/risks/all/${orgId}`);
            setRisks(response.data.data || []);
        } catch (error) {
            // console.error removed
        } finally {
            setLoading(false);
        }
    };

    const handleDragStart = (e, risk) => {
        e.dataTransfer.setData('riskId', risk._id);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = async (e, newStatus) => {
        e.preventDefault();
        const riskId = e.dataTransfer.getData('riskId');
        
        try {
            await axios.patch(`${API_URL}/risks/${riskId}/status`, { status: newStatus });
            fetchRisks(); // Refresh the data
        } catch (error) {
            // console.error removed
        }
    };

    const RiskCard = ({ risk }) => (
        <div
            className="card mb-2 risk-card"
            draggable
            onDragStart={(e) => handleDragStart(e, risk)}
        >
            <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="mb-0">{risk.displayDetails?.title}</h6>
                    {risk.assigneeId ? (
                        <span className="badge badge-info">
                            <i className="fa fa-user mr-1"></i>
                            {users.find(u => u.id === risk.assigneeId)?.name}
                        </span>
                    ) : (
                        <span className="badge badge-light">
                            <i className="fa fa-user-slash mr-1"></i>
                            Unassigned
                        </span>
                    )}
                </div>
                <div className="d-flex justify-content-between">
                    <div className="dropdown">
                        <button 
                            className="btn btn-sm btn-outline-warning dropdown-toggle" 
                            type="button" 
                            data-toggle="dropdown"
                        >
                            <i className="fa fa-exclamation-triangle"></i>
                        </button>
                        <div className="dropdown-menu dropdown-menu-right">
                            {severityLevels.map(severity => (
                                <button
                                    key={severity.value}
                                    className="dropdown-item d-flex align-items-center"
                                    onClick={() => handleSeverityUpdate(risk._id, severity.value)}
                                    disabled={risk.severity === severity.value}
                                >
                                    <i className={`fa fa-circle mr-2 text-${severity.class}`}></i>
                                    {severity.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="d-flex align-items-center mb-2">
                    <span className={`badge badge-${severityLevels.find(s => s.value === risk.severity)?.class}`}>
                        {risk.severity}
                    </span>
                    <small className="text-muted ml-2">
                        {categories.find(c => c.value === risk.category)?.label}
                    </small>
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="d-flex justify-content-center p-5">
                <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="kanban-board d-flex overflow-auto p-3">
            {riskStatuses.map(status => (
                <div 
                    key={status.value} 
                    className="kanban-column mx-2" 
                    style={{ minWidth: '300px' }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, status.value)}
                >
                    <div className={`card border-${status.class}`}>
                        <div className={`card-header bg-${status.class} text-white d-flex align-items-center`}>
                            <i className={`fa ${status.icon} mr-2`}></i>
                            <h5 className="mb-0">{status.label}</h5>
                            <span className="badge badge-light ml-2">
                                {risks.filter(r => r.status === status.value).length}
                            </span>
                        </div>
                        <div 
                            className="card-body"
                            style={{ height: 'calc(100vh - 200px)', overflowY: 'auto' }}
                        >
                            {risks
                                .filter(risk => risk.status === status.value)
                                .map(risk => (
                                    <RiskCard key={risk._id} risk={risk} />
                                ))
                            }
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default RiskKanbanView; 